import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { useProgram } from "../../context/ProgramContext";
import dayjs from "dayjs";
import ExpressPurchaseButton from "../../components/ExpressPurchaseButton";
import { getSeasonPhase, getSeasonWorkoutGuidance } from "../../lib/seasonProfile";
import { buildSwotPriorityPayload } from "../../lib/swotPriorityRouter";

const unique = (arr) => [...new Set((arr || []).filter(Boolean))];
const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

const parsePositionIdsFromSportInfoRow = (row) => {
  if (!row || typeof row !== "object") return { primaryId: null, secondaryId: null };
  const primaryId = String(row.position || "").trim() || null;
  const secondaryId = String(row.secondary_position || "").trim() || null;
  return {
    primaryId,
    secondaryId: secondaryId && secondaryId !== primaryId ? secondaryId : null
  };
};

const tokenize = (value) => String(value || "")
  .toLowerCase()
  .split(/[^a-z0-9]+/g)
  .filter((token) => token.length >= 3);

const inferBaseballDomainsFromPositionLabel = (label) => {
  const lower = String(label || "").toLowerCase();
  const domains = [];
  if (lower.includes("pitch")) domains.push("pitching");
  if (lower.includes("catch")) domains.push("hitting", "fielding");
  if (
    lower.includes("first") ||
    lower.includes("second") ||
    lower.includes("third") ||
    lower.includes("short") ||
    lower.includes("infield") ||
    lower.includes("outfield") ||
    lower.includes("center") ||
    lower.includes("left") ||
    lower.includes("right")
  ) domains.push("fielding", "hitting");
  if (domains.length === 0) domains.push("hitting", "pitching", "fielding");
  return unique(domains);
};

const BASEBALL_DOMAIN_KEYWORDS = {
  hitting: ["hit", "hitting", "bat", "swing", "barrel", "contact", "plate", "approach", "exit velo", "launch"],
  pitching: ["pitch", "pitching", "velo", "velocity", "command", "bullpen", "release", "arm", "spin", "fastball", "slider", "curve", "changeup"],
  fielding: ["field", "fielding", "glove", "infield", "outfield", "footwork", "range", "ground", "fly", "throw", "defense", "transfer"],
};

const filterMapByBaseballDomain = (scoreMap, baseballFocusDomain) => {
  const source = scoreMap && typeof scoreMap === "object" ? scoreMap : {};
  if (baseballFocusDomain === "all") return source;
  const keywords = BASEBALL_DOMAIN_KEYWORDS[baseballFocusDomain] || [];
  const entries = Object.entries(source);
  const filtered = Object.fromEntries(
    entries.filter(([key]) => {
      const lower = String(key || "").toLowerCase();
      return keywords.some((kw) => lower.includes(kw));
    })
  );
  return Object.keys(filtered).length > 0 ? filtered : source;
};

const filterFocusAreasByBaseballDomain = (focusAreas, baseballFocusDomain) => {
  const list = Array.isArray(focusAreas) ? focusAreas : [];
  if (baseballFocusDomain === "all") return list;
  const keywords = BASEBALL_DOMAIN_KEYWORDS[baseballFocusDomain] || [];
  const filtered = list.filter((item) => {
    const lower = String(item || "").toLowerCase();
    return keywords.some((kw) => lower.includes(kw));
  });
  return filtered.length > 0 ? filtered : list;
};

const entryMatchesBaseballDomain = (entry, baseballFocusDomain) => {
  if (baseballFocusDomain === "all") return true;
  const target = String(baseballFocusDomain || "").toLowerCase();
  const domains = Array.isArray(entry?.baseball_domains)
    ? entry.baseball_domains.map((d) => String(d || "").toLowerCase())
    : [];
  if (domains.includes(target)) return true;
  const keywords = BASEBALL_DOMAIN_KEYWORDS[target] || [];
  const haystack = `${entry?.section || ""} ${entry?.item || ""} ${(entry?.action_texts || []).join(" ")}`.toLowerCase();
  return keywords.some((kw) => haystack.includes(kw));
};

const filterSwotPriorityPayloadByBaseballDomain = (swotPayload, baseballFocusDomain) => {
  if (!swotPayload || baseballFocusDomain === "all") return swotPayload;
  const filterList = (list) => (Array.isArray(list) ? list.filter((entry) => entryMatchesBaseballDomain(entry, baseballFocusDomain)) : []);
  const filteredSkillPriorities = filterList(swotPayload.skill_priorities || []).slice(0, 3);
  const filterCategoryMap = (categoryMap) => {
    if (!categoryMap || typeof categoryMap !== "object") return null;
    return {
      strengths: filterList(categoryMap.strengths || []),
      weaknesses: filterList(categoryMap.weaknesses || []),
      opportunities: filterList(categoryMap.opportunities || []),
      threats: filterList(categoryMap.threats || []),
    };
  };
  const filteredCategories = filterCategoryMap(swotPayload.skill_categories);

  return {
    ...swotPayload,
    skill_priorities: filteredSkillPriorities,
    season_priority_focus_items: filteredSkillPriorities.map((entry) => entry.item).filter(Boolean),
    skill_categories: filteredCategories
  };
};

const filterCombineRowsByBaseballDomain = (rows, baseballFocusDomain, key = "drill_name") => {
  const list = Array.isArray(rows) ? rows : [];
  if (baseballFocusDomain === "all") return list;
  const keywords = BASEBALL_DOMAIN_KEYWORDS[baseballFocusDomain] || [];
  const filtered = list.filter((row) => {
    const text = String(row?.[key] || "").toLowerCase();
    return keywords.some((kw) => text.includes(kw));
  });
  return filtered.length > 0 ? filtered : list;
};

const toFocusAreaArray = (value) => {
  if (Array.isArray(value)) return value.map((v) => String(v || "").trim()).filter(Boolean);
  if (typeof value === "string") {
    return value.split(",").map((v) => v.trim()).filter(Boolean);
  }
  return [];
};

const splitSwotPrioritiesByLane = (swotPayload, lane, sport) => {
  if (!swotPayload || !lane || lane.id === "all") return swotPayload;
  const laneName = String(lane.label || "").trim();
  if (!laneName) return swotPayload;
  const laneTokens = tokenize(laneName);

  const toSearchText = (entry) => [
    entry?.section,
    entry?.item,
    ...(Array.isArray(entry?.action_texts) ? entry.action_texts : [])
  ].join(" ").toLowerCase();
  const matchesByToken = (entry) => {
    const haystack = toSearchText(entry);
    if (laneTokens.length === 0) return true;
    return laneTokens.some((token) => haystack.includes(token));
  };

  const filterList = (list) => {
    if (!Array.isArray(list)) return [];
    if (sport === "baseball") {
      const laneDomains = inferBaseballDomainsFromPositionLabel(laneName);
      const filteredByDomain = list.filter((entry) => {
        const domains = Array.isArray(entry?.baseball_domains) ? entry.baseball_domains : [];
        return domains.some((domain) => laneDomains.includes(domain));
      });
      if (filteredByDomain.length > 0) return filteredByDomain;
      // Do not leak unrelated baseball domain priorities into another lane.
      const filteredByToken = list.filter(matchesByToken);
      return filteredByToken.length > 0 ? filteredByToken : [];
    }
    const filteredByToken = list.filter(matchesByToken);
    return filteredByToken.length > 0 ? filteredByToken : list;
  };

  const laneSkillPriorities = filterList(swotPayload.skill_priorities || []).slice(0, 3);
  const laneSkillsById = new Set(laneSkillPriorities.map((entry) => entry.id).filter(Boolean));
  const filterCategoryMap = (categoryMap) => {
    if (!categoryMap || typeof categoryMap !== "object") return null;
    return {
      strengths: (categoryMap.strengths || []).filter((entry) => laneSkillsById.has(entry.id) || matchesByToken(entry)),
      weaknesses: (categoryMap.weaknesses || []).filter((entry) => laneSkillsById.has(entry.id) || matchesByToken(entry)),
      opportunities: (categoryMap.opportunities || []).filter((entry) => laneSkillsById.has(entry.id) || matchesByToken(entry)),
      threats: (categoryMap.threats || []).filter((entry) => laneSkillsById.has(entry.id) || matchesByToken(entry)),
    };
  };
  const laneSkillCategories = filterCategoryMap(swotPayload.skill_categories);

  return {
    ...swotPayload,
    skill_priorities: laneSkillPriorities,
    season_priority_focus_items: laneSkillPriorities.map((entry) => entry.item).filter(Boolean),
    skill_categories: laneSkillCategories
  };
};

const pickPreferredSwotRow = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const favorited = rows.find((row) => row?.favorited);
  if (favorited) return favorited;
  return rows[0] || null;
};

const mergeSwotPriorityPayloads = (payloads) => {
  const valid = (Array.isArray(payloads) ? payloads : []).filter(Boolean);
  if (valid.length === 0) return null;

  const byKey = new Map();
  const athleticByKey = new Map();
  const mergedCategories = {
    strengths: [],
    weaknesses: [],
    opportunities: [],
    threats: [],
  };
  const mergedAthleticCategories = {
    strengths: [],
    weaknesses: [],
    opportunities: [],
    threats: [],
  };
  const firstPendingByCategory = {};

  valid.forEach((payload) => {
    (payload.skill_priorities || []).forEach((entry) => {
      const key = String(entry?.id || entry?.item || "").trim();
      if (!key || byKey.has(key)) return;
      byKey.set(key, entry);
    });
    (payload.athletic_priorities || []).forEach((entry) => {
      const key = String(entry?.id || entry?.item || "").trim();
      if (!key || athleticByKey.has(key)) return;
      athleticByKey.set(key, entry);
    });

    const categories = payload.skill_categories || {};
    ["strengths", "weaknesses", "opportunities", "threats"].forEach((category) => {
      (categories[category] || []).forEach((entry) => {
        const key = String(entry?.id || `${category}:${entry?.item || ""}`).trim();
        if (!key) return;
        const exists = mergedCategories[category].some((current) => {
          const currentKey = String(current?.id || `${category}:${current?.item || ""}`).trim();
          return currentKey === key;
        });
        if (!exists) mergedCategories[category].push(entry);
      });
    });
    const athleticCategories = payload.athletic_categories || {};
    ["strengths", "weaknesses", "opportunities", "threats"].forEach((category) => {
      (athleticCategories[category] || []).forEach((entry) => {
        const key = String(entry?.id || `${category}:${entry?.item || ""}`).trim();
        if (!key) return;
        const exists = mergedAthleticCategories[category].some((current) => {
          const currentKey = String(current?.id || `${category}:${current?.item || ""}`).trim();
          return currentKey === key;
        });
        if (!exists) mergedAthleticCategories[category].push(entry);
      });
    });

    if (payload.first_pending_by_category && typeof payload.first_pending_by_category === "object") {
      Object.entries(payload.first_pending_by_category).forEach(([category, value]) => {
        if (!firstPendingByCategory[category] && value) {
          firstPendingByCategory[category] = value;
        }
      });
    }
  });

  const mergedPriorities = Array.from(byKey.values()).slice(0, 6);
  const mergedAthleticPriorities = Array.from(athleticByKey.values()).slice(0, 3);

  return {
    ...valid[0],
    skill_priorities: mergedPriorities,
    athletic_priorities: mergedAthleticPriorities,
    season_priority_focus_items: mergedPriorities.map((entry) => entry.item).filter(Boolean),
    athletic_priority_focus_items: mergedAthleticPriorities.map((entry) => entry.item).filter(Boolean),
    skill_categories: mergedCategories,
    athletic_categories: mergedAthleticCategories,
    first_pending_by_category: firstPendingByCategory,
    active_focus_items: unique(valid.flatMap((payload) => payload.active_focus_items || [])),
    primary_focus_items: unique(valid.flatMap((payload) => payload.primary_focus_items || [])),
  };
};

const toSkillOnlySwotPayload = (payload) => {
  if (!payload) return null;
  const emptyCategories = { strengths: [], weaknesses: [], opportunities: [], threats: [] };
  const skillPriorities = Array.isArray(payload.skill_priorities) ? payload.skill_priorities : [];
  return {
    ...payload,
    skill_priorities: skillPriorities,
    season_priority_focus_items: skillPriorities.map((entry) => entry?.item).filter(Boolean),
    athletic_priorities: [],
    athletic_priority_focus_items: [],
    athletic_categories: emptyCategories
  };
};

export default function SkillWorkouts({ userId }) {
  const navigate = useNavigate();
  const { selectedProgram, setSelectedProgram } = useProgram();
  const [weeklyPlan, setWeeklyPlan] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [modeledPlayer, setModeledPlayer] = useState(null);
  const [workoutLength, setWorkoutLength] = useState("60");
  const [todaysWorkouts, setTodaysWorkouts] = useState([]);
  const [recentWorkouts, setRecentWorkouts] = useState([]);
  const [monthlyOutlines, setMonthlyOutlines] = useState([]);
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [selectedWeeklyWorkouts, setSelectedWeeklyWorkouts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dailyWorkout, setDailyWorkout] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showWorkoutModal, setShowWorkoutModal] = useState(false);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [allSwots, setAllSwots] = useState([]);
  const [selectedSwotId, setSelectedSwotId] = useState("");
  const [userGrade, setUserGrade] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [baseballFocusDomain, setBaseballFocusDomain] = useState("all");
  const [positionLaneOptions, setPositionLaneOptions] = useState([]);
  const [selectedPositionLaneId, setSelectedPositionLaneId] = useState("all");
  const [hasProgramWorkoutAccess, setHasProgramWorkoutAccess] = useState(false);
  const [checkingProgramWorkoutAccess, setCheckingProgramWorkoutAccess] = useState(false);

  // Helper function to render drill blocks
  const renderBlock = (blockName, drills) => {
    if (!drills || drills.length === 0) return null;

    return (
      <div className="mb-4">
        <h3 className="text-lg font-semibold">{blockName}</h3>
        <ul className="list-disc list-inside">
          {drills.map((drill, idx) => (
            <li key={idx} className="mb-2">
              <p className="font-medium">{drill.drill_name} ({drill.duration_minutes} min)</p>
              <p className="text-gray-700">{drill.description}</p>
              <p className="text-gray-500 text-sm">
                Sets: {drill.sets} | Reps: {drill.reps.join(", ")}
              </p>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // Fetch weekly skill workouts
  const fetchWeeklyWorkouts = async (monthlyId) => {
    try {
      const { data, error } = await supabase
        .from("weekly_skill_workouts")
        .select("*")
        .eq("plan_id", monthlyId)
        .order("week_number", { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error("Error fetching weekly workouts:", err);
      return [];
    }
  };

  const parseJsonArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  };

  const normalizeSwotAction = (action) => {
    if (typeof action === "string") return { text: action.trim(), completed: false };
    if (action && typeof action === "object") {
      return {
        text: String(action.text || action.item || action.action || "").trim(),
        completed: Boolean(action.completed),
      };
    }
    return { text: "", completed: false };
  };

  const normalizeSwotEntries = (entries) => {
    if (!Array.isArray(entries)) return [];
    return entries
      .map((entry) => {
        const actions = Array.isArray(entry?.actions)
          ? entry.actions.map(normalizeSwotAction).filter((a) => a.text !== "")
          : [];
        const completedByActions = actions.length > 0 && actions.every((a) => a.completed);
        return {
          ...entry,
          item: String(entry?.item || "").trim(),
          completed: Boolean(entry?.completed) || completedByActions,
          actions,
        };
      })
      .filter((entry) => entry.item !== "");
  };

  const entryToNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : NaN;
  };

  const getSeasonPrioritiesFromEntries = (entries, category) => {
    return (entries || [])
      .filter((entry) => entry.priority)
      .sort((a, b) => {
        const rankA = Number.isFinite(entryToNumber(a?.priorityRank)) ? entryToNumber(a?.priorityRank) : 999;
        const rankB = Number.isFinite(entryToNumber(b?.priorityRank)) ? entryToNumber(b?.priorityRank) : 999;
        return rankA - rankB;
      })
      .slice(0, 3)
      .map((entry) => ({
        id: entry.id || null,
        category,
        section: entry.section || "General",
        item: entry.item,
        priorityRank: Number.isFinite(entryToNumber(entry?.priorityRank)) ? entryToNumber(entry.priorityRank) : null,
        completed: Boolean(entry.completed),
        actions: (Array.isArray(entry?.actions) ? entry.actions : [])
          .map(normalizeSwotAction)
          .filter((action) => action.text !== "")
      }));
  };

  const getPendingActions = (entry) => (entry.actions || []).filter((a) => !a.completed);

  const buildActiveSwotPayload = (swotRow) => {
    if (!swotRow) return null;
    const categories = ["strengths", "weaknesses", "opportunities", "threats"];
    const payload = {
      id: swotRow.id,
      program_id: swotRow.program_id,
      favorited: swotRow.favorited,
      updated_at: swotRow.updated_at,
      sport: swotRow.programs?.sport || null,
      program_name: swotRow.programs?.name || null,
    };

    const firstPendingByCategory = {};
    const activeFocusItems = [];
    const seasonPriorities = [];

    categories.forEach((category) => {
      const normalizedEntries = normalizeSwotEntries(parseJsonArray(swotRow[category]));
      const categoryPriorities = getSeasonPrioritiesFromEntries(normalizedEntries, category);
      seasonPriorities.push(...categoryPriorities);
      const withPending = normalizedEntries
        .filter((entry) => !entry.completed)
        .map((entry) => ({ ...entry, actions: getPendingActions(entry) }))
        .filter((entry) => entry.actions.length > 0);
      payload[category] = normalizedEntries;
      payload[`${category}_actionable`] = withPending;
      if (withPending.length > 0) {
        firstPendingByCategory[category] = {
          item: withPending[0].item,
          action: withPending[0].actions[0]?.text || null,
        };
        activeFocusItems.push(withPending[0].item);
      }
    });

    const rankedSeasonPriorities = seasonPriorities
      .sort((a, b) => {
        const rankA = Number.isFinite(entryToNumber(a?.priorityRank)) ? entryToNumber(a?.priorityRank) : 999;
        const rankB = Number.isFinite(entryToNumber(b?.priorityRank)) ? entryToNumber(b?.priorityRank) : 999;
        return rankA - rankB;
      })
      .slice(0, 3);
    const activeSeasonPriorities = rankedSeasonPriorities.filter((entry) => !entry.completed);
    const seasonPriorityFocusItems = activeSeasonPriorities.map((entry) => entry.item).filter(Boolean);

    payload.season_priorities_all = rankedSeasonPriorities;
    payload.season_priorities = activeSeasonPriorities;
    payload.season_priority_focus_items = seasonPriorityFocusItems;
    payload.completed_priority_items = rankedSeasonPriorities.filter((entry) => entry.completed);
    payload.first_pending_by_category = firstPendingByCategory;
    payload.active_focus_items = [...new Set(activeFocusItems)];
    payload.primary_focus_items = seasonPriorityFocusItems.length > 0
      ? seasonPriorityFocusItems
      : payload.active_focus_items;
    return payload;
  };

  const getFocusAreasFromSwot = (swotRow) => {
    const activePayload = buildActiveSwotPayload(swotRow);
    if (!activePayload) return [];
    if ((activePayload.primary_focus_items || []).length > 0) return activePayload.primary_focus_items;
    return [];
  };

  // Fetch user's followed programs
  useEffect(() => {
    async function fetchPrograms() {
      const { data, error } = await supabase
        .from("user_program_follows")
        .select(`
          program_id, 
          programs (*)
        `)
        .eq("user_id", userId);

      if (error) {
        console.error("Supabase Error:", error);
      } else {
        setPrograms(data || []);
      }
    }
    fetchPrograms();
  }, [userId]);

  // Fetch athlete SWOT analyses (draft + published)
  useEffect(() => {
    async function fetchSwots() {
      if (!userId) return;
      const normalizedEmail = normalizeEmail(userEmail);
      const baseQuery = () => {
        let query = supabase
          .from("athlete_swot")
          .select(`
            id,
            program_id,
            position_id,
            favorited,
            strengths,
            weaknesses,
            opportunities,
            threats,
            updated_at,
            programs:program_id (name, sport),
            position:positions!athlete_swot_position_id_fkey(name)
          `)
          .in("status", ["draft", "published"])
          .order("updated_at", { ascending: false });

        if (normalizedEmail) {
          query = query.or(`athlete_id.eq.${userId},prebuilt_email.ilike.${normalizedEmail}`);
        } else {
          query = query.eq("athlete_id", userId);
        }
        return query;
      };

      let data = [];
      let error = null;

      if (selectedProgram?.id) {
        const res = await baseQuery().eq("program_id", selectedProgram.id);
        data = res.data || [];
        error = res.error || null;
      }

      if (!error && data.length === 0 && selectedProgram?.sport) {
        const res = await baseQuery().eq("programs.sport", selectedProgram.sport);
        data = res.data || [];
        error = res.error || null;
      }

      console.log("[SkillWorkouts] SWOT fetch", {
        userId,
        selectedProgramId: selectedProgram?.id || null,
        selectedProgramSport: selectedProgram?.sport || null,
        userEmail: userEmail || null,
        swotCount: data?.length || 0,
        swotSample: data?.[0] || null
      });

      if (error) {
        console.error("Error fetching SWOT analyses:", error);
        setAllSwots([]);
        return;
      }
      setAllSwots(data || []);
    }
    fetchSwots();
  }, [userId, userEmail, selectedProgram?.id]);

  useEffect(() => {
    async function fetchUserGrade() {
      if (!userId) return;
      const { data, error } = await supabase
        .from("users")
        .select("grade, email")
        .eq("id", userId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching user grade:", error);
        setUserGrade(null);
        setUserEmail("");
        return;
      }
      setUserGrade(data?.grade ?? null);
      setUserEmail(data?.email ?? "");
    }
    fetchUserGrade();
  }, [userId]);

  // Set default program if none selected
  useEffect(() => {
    if (!selectedProgram && programs.length > 0 && setSelectedProgram) {
      const defaultProgram = programs[0];
      setSelectedProgram({
        ...(defaultProgram.programs || {}),
        id: defaultProgram.program_id,
      });
    }
  }, [programs, selectedProgram, setSelectedProgram]);

  useEffect(() => {
    async function fetchProgramWorkoutAccess() {
      if (!userId || !selectedProgram?.id) {
        setHasProgramWorkoutAccess(false);
        return;
      }

      setCheckingProgramWorkoutAccess(true);
      try {
        const today = dayjs().format("YYYY-MM-DD");
        const [
          { data: purchaseRows, error: purchaseError },
          { data: devRows, error: devError },
          { data: skillGenRows, error: skillGenError },
        ] = await Promise.all([
          supabase
            .from("program_purchases")
            .select("roster_limit, start_date, end_date")
            .eq("program_id", selectedProgram.id)
            .eq("status", "active")
            .lte("start_date", today)
            .gte("end_date", today)
            .order("end_date", { ascending: false })
            .limit(1),
          supabase
            .from("athlete_development_programs")
            .select("id, start_date, end_date, status")
            .eq("athlete_id", userId)
            .eq("program_id", selectedProgram.id)
            .eq("status", "active")
            .lte("start_date", today)
            .gte("end_date", today)
            .limit(1),
          supabase
            .from("athlete_offseason_skill_generators")
            .select("id, start_date, end_date, status")
            .eq("athlete_id", userId)
            .eq("program_id", selectedProgram.id)
            .eq("status", "active")
            .lte("start_date", today)
            .gte("end_date", today)
            .limit(1),
        ]);

        if (purchaseError) throw purchaseError;
        if (devError) throw devError;
        if (skillGenError && skillGenError.code !== "42P01") throw skillGenError;

        let hasProgramPurchaseAccess = false;
        const rosterLimit = Number(purchaseRows?.[0]?.roster_limit || 0);
        if (rosterLimit > 0) {
          const { data: rosteredRows, error: rosterError } = await supabase
            .from("rostered_users")
            .select("user_id, created_at, teams!inner(program_id)")
            .eq("teams.program_id", selectedProgram.id)
            .order("created_at", { ascending: true })
            .limit(rosterLimit);

          if (rosterError) throw rosterError;
          hasProgramPurchaseAccess = (rosteredRows || []).some((row) => String(row.user_id) === String(userId));
        }

        const hasActiveAthleteProgram = (devRows || []).length > 0;
        const hasActiveSkillGenerator = skillGenError?.code === "42P01" ? false : (skillGenRows || []).length > 0;

        setHasProgramWorkoutAccess(hasProgramPurchaseAccess || hasActiveAthleteProgram || hasActiveSkillGenerator);
      } catch (err) {
        console.error("Error checking program workout access:", err);
        setHasProgramWorkoutAccess(false);
      } finally {
        setCheckingProgramWorkoutAccess(false);
      }
    }

    fetchProgramWorkoutAccess();
  }, [selectedProgram?.id, userId]);

  const selectedProgramSport = selectedProgram?.sport?.trim().toLowerCase() || "";
  const swotsForSelectedSport = allSwots.filter((s) => {
    const sport = s.programs?.sport?.trim().toLowerCase() || "";
    return sport && sport === selectedProgramSport;
  });
  const swotsForSelectedProgram = swotsForSelectedSport.filter(
    (s) => String(s.program_id || "") === String(selectedProgram?.id || "")
  );

  const selectedSwot = swotsForSelectedProgram.find((s) => String(s.id) === String(selectedSwotId))
    || swotsForSelectedSport.find((s) => String(s.id) === String(selectedSwotId))
    || null;
  const seasonPhase = getSeasonPhase(selectedProgram);
  const seasonGuidance = getSeasonWorkoutGuidance(seasonPhase);
  const parsedWorkoutLength = Number(workoutLength);
  const effectiveWorkoutLength = Number.isFinite(parsedWorkoutLength) && parsedWorkoutLength > 0
    ? parsedWorkoutLength
    : seasonGuidance.recommendedWorkoutLength;
  const selectedSwotPayload = selectedSwot ? buildActiveSwotPayload(selectedSwot) : null;
  const selectedSwotPriorityPayload = selectedSwot
    ? toSkillOnlySwotPayload(buildSwotPriorityPayload(selectedSwot, { sport: selectedProgramSport, baseballFocusDomain }))
    : null;
  const selectedPositionLane = useMemo(() => (
    positionLaneOptions.find((lane) => lane.id === selectedPositionLaneId) || null
  ), [positionLaneOptions, selectedPositionLaneId]);
  const hasSecondaryPosition = positionLaneOptions.length > 1;
  const swotLaneContext = useMemo(() => {
    if (!selectedProgramSport) return null;
    const programScoped = swotsForSelectedProgram.length > 0 ? swotsForSelectedProgram : swotsForSelectedSport;

    const laneRows = positionLaneOptions.map((lane) => {
      const laneId = String(lane.id || "").trim();
      const exact = pickPreferredSwotRow(
        programScoped.filter((row) => String(row.position_id || "") === laneId)
      );
      return {
        lane,
        row: exact || selectedSwot || null,
      };
    });

    const lanePayloads = laneRows.map(({ lane, row }) => {
      if (!row) return { lane, payload: null, row: null };
      const laneBasePayload = toSkillOnlySwotPayload(buildSwotPriorityPayload(row, {
        sport: selectedProgramSport,
        baseballFocusDomain
      }));
      const scopedLanePayload = row?.position_id
        ? laneBasePayload
        : splitSwotPrioritiesByLane(laneBasePayload, lane, selectedProgramSport);
      return { lane, payload: scopedLanePayload, row };
    });

    return {
      laneRows,
      lanePayloads,
      mergedPayload: toSkillOnlySwotPayload(mergeSwotPriorityPayloads(lanePayloads.map((entry) => entry.payload))),
    };
  }, [
    selectedProgramSport,
    swotsForSelectedProgram,
    swotsForSelectedSport,
    positionLaneOptions,
    selectedSwot,
    baseballFocusDomain
  ]);
  const swotPayloadByLaneId = useMemo(() => {
    const laneEntries = swotLaneContext?.lanePayloads || [];
    return Object.fromEntries(
      laneEntries
        .filter((entry) => entry?.lane?.id)
        .map((entry) => [String(entry.lane.id), entry.payload || null])
    );
  }, [swotLaneContext]);
  const laneByDomainMap = useMemo(() => {
    const entries = swotLaneContext?.lanePayloads || [];
    const map = {};
    entries.forEach(({ lane }) => {
      const domains = inferBaseballDomainsFromPositionLabel(lane?.label || "");
      domains.forEach((domain) => {
        if (!map[domain]) map[domain] = lane;
      });
    });
    return map;
  }, [swotLaneContext]);
  const effectiveDailyLane = useMemo(() => {
    if (!hasSecondaryPosition) return null;
    if (selectedProgramSport !== "baseball") return null;
    if (baseballFocusDomain === "all") return null;
    return laneByDomainMap[baseballFocusDomain] || positionLaneOptions[0] || null;
  }, [
    hasSecondaryPosition,
    selectedProgramSport,
    baseballFocusDomain,
    laneByDomainMap,
    positionLaneOptions
  ]);
  const effectiveDailySwotPriorityPayload = useMemo(() => {
    const selectedLanePayload = effectiveDailyLane?.id
      ? swotPayloadByLaneId[String(effectiveDailyLane.id)] || null
      : null;
    const fallbackPayload = selectedLanePayload
      || swotLaneContext?.mergedPayload
      || selectedSwotPriorityPayload;
    if (!fallbackPayload) return null;
    return selectedProgramSport === "baseball"
      ? filterSwotPriorityPayloadByBaseballDomain(fallbackPayload, baseballFocusDomain)
      : fallbackPayload;
  }, [
    effectiveDailyLane,
    swotPayloadByLaneId,
    swotLaneContext,
    selectedSwotPriorityPayload,
    selectedProgramSport,
    baseballFocusDomain
  ]);
  const effectiveMonthlySwotPriorityPayload = useMemo(() => (
    swotLaneContext?.mergedPayload || selectedSwotPriorityPayload || null
  ), [swotLaneContext, selectedSwotPriorityPayload]);
  const effectiveSwotPriorityPayloadForDaily = useMemo(() => (
    effectiveDailySwotPriorityPayload
  ), [effectiveDailySwotPriorityPayload]);
  const dualPositionMonthlyContext = useMemo(() => {
    if (!hasSecondaryPosition) return null;
    const [primaryLane, secondaryLane] = positionLaneOptions;
    const primaryPayload = swotPayloadByLaneId[String(primaryLane?.id || "")] || null;
    const secondaryPayload = swotPayloadByLaneId[String(secondaryLane?.id || "")] || null;
    if (!primaryPayload && !secondaryPayload) return null;
    return {
      enabled: true,
      lane_weighting_model: "Primary position receives 60% of monthly emphasis. Secondary position receives 40%.",
      allocation_model: "Inside each lane apply 50/25/25: 50% priorities, 25% strengths, 25% weaknesses.",
      optional_mixed_session: "Optional mixed session can be used for micro-skills and game-read work.",
      lanes: [
        {
          lane_key: "primary_position",
          position_id: primaryLane?.id || null,
          position_name: primaryLane?.label || "Primary",
          lane_weight: 0.6,
          lane_weight_percent: 60,
          swot_id: swotLaneContext?.laneRows?.find((entry) => entry?.lane?.id === primaryLane?.id)?.row?.id || null,
          preferred_domains: selectedProgramSport === "baseball"
            ? inferBaseballDomainsFromPositionLabel(primaryLane?.label || "")
            : [],
          focus_items: primaryPayload?.season_priority_focus_items || [],
          skill_priorities: primaryPayload?.skill_priorities || [],
          skill_categories: primaryPayload?.skill_categories || null,
        },
        {
          lane_key: "secondary_position",
          position_id: secondaryLane?.id || null,
          position_name: secondaryLane?.label || "Secondary",
          lane_weight: 0.4,
          lane_weight_percent: 40,
          swot_id: swotLaneContext?.laneRows?.find((entry) => entry?.lane?.id === secondaryLane?.id)?.row?.id || null,
          preferred_domains: selectedProgramSport === "baseball"
            ? inferBaseballDomainsFromPositionLabel(secondaryLane?.label || "")
            : [],
          focus_items: secondaryPayload?.season_priority_focus_items || [],
          skill_priorities: secondaryPayload?.skill_priorities || [],
          skill_categories: secondaryPayload?.skill_categories || null,
        }
      ]
    };
  }, [hasSecondaryPosition, positionLaneOptions, selectedProgramSport, swotPayloadByLaneId, swotLaneContext]);
  const weeklyLaneLegend = useMemo(() => {
    if (selectedProgramSport !== "baseball" || !hasSecondaryPosition) return [];
    const colors = [
      { chip: "bg-blue-100 text-blue-700 border-blue-200", dot: "bg-blue-500" },
      { chip: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
    ];
    return positionLaneOptions.slice(0, 2).map((lane, idx) => ({
      id: lane.id,
      label: lane.label,
      preferredDomains: inferBaseballDomainsFromPositionLabel(lane.label),
      ...colors[idx]
    }));
  }, [selectedProgramSport, hasSecondaryPosition, positionLaneOptions]);
  const focusAreaChipClass = (area) => {
    if (weeklyLaneLegend.length === 0) return "bg-slate-100 text-slate-600 border-slate-200";
    const areaText = String(area || "").toLowerCase();
    const matchingLegend = weeklyLaneLegend.find((lane) => {
      const keywords = lane.preferredDomains.flatMap((domain) => BASEBALL_DOMAIN_KEYWORDS[domain] || []);
      return keywords.some((kw) => areaText.includes(kw));
    });
    return matchingLegend?.chip || "bg-slate-100 text-slate-600 border-slate-200";
  };
  const priorityPromptDirectives = {
    priority_execution_order: "Keep Priority #1 active as the weekly anchor until coach marks it completed. Do not rotate to Priority #2/#3 by week number.",
    session_focus_rotation: "Do not skip ahead in the priority queue. Advance only on completion check-off.",
    allocation_model: "Allocate approximately 50% of session to active priority queue, 25% to strengths reinforcement, 25% to weaknesses correction.",
    spiral_curriculum: "Maintain spiral curriculum by revisiting prior concepts while increasing difficulty and context each week.",
    opening_drill_rule: "The first drills of each workout must directly target the workout's main priority focus.",
    weekly_focus_pattern: "Weekly sequence should be Priority #1 Micro-Point 1, then Priority #1 Micro-Point 2, then Priority #1 Micro-Point 3 before moving to next priority.",
    completion_progression_rule: "When an item or priority is marked completed, stop using it as workout focus and automatically continue with the next active item/priority.",
    micro_point_step_progression: "Inside each micro-point, progress checklist steps in order (step 1 -> step 2 -> step 3). Keep cycling this structure until skill acquisition is marked complete.",
    compatibility_rule: "Strength and weakness reinforcement must stay compatible with the selected lane and domain (e.g., hitting lanes use hitting-compatible strengths/weaknesses)."
  };

  useEffect(() => {
    if (!selectedProgramSport) {
      setSelectedSwotId("");
      return;
    }

    const selectableSwots = swotsForSelectedProgram.length > 0 ? swotsForSelectedProgram : swotsForSelectedSport;
    if (selectableSwots.length === 0) {
      setSelectedSwotId("");
      return;
    }

    const currentStillValid = selectableSwots.some((s) => String(s.id) === String(selectedSwotId));
    if (currentStillValid) return;

    const favorited = selectableSwots.find((s) => s.favorited);
    setSelectedSwotId(String((favorited || selectableSwots[0]).id));
  }, [selectedProgramSport, selectedSwotId, swotsForSelectedProgram, swotsForSelectedSport]);

  // Fetch Modeled Player Data
  useEffect(() => {
    async function fetchModeledPlayerData() {
      if (!userId || !selectedProgram?.sport) {
        setModeledPlayer(null);
        setPositionLaneOptions([]);
        setSelectedPositionLaneId("all");
        return;
      }

      try {
        const sportName = selectedProgram.sport.trim();

        // Get the Sport Model ID using the Name
        const { data: sportModel, error: modelError } = await supabase
          .from("development_sports")
          .select("*")
          .ilike("name", sportName)
          .limit(1)
          .maybeSingle();

        if (modelError) throw modelError;

        if (!sportModel) {
          console.warn(`No sport found in database matching: ${sportName}`);
          setModeledPlayer(null);
          setPositionLaneOptions([]);
          setSelectedPositionLaneId("all");
          return;
        }

        // Get the User Info using the Sport ID
        const { data: userInfo, error: userError } = await supabase
          .from("user_sport_info")
          .select("modeled_player, position, secondary_position")
          .eq("user_id", userId)
          .eq("sport", sportModel.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (userError) throw userError;

        setModeledPlayer(userInfo?.modeled_player || "No Athlete Found");
        const { primaryId, secondaryId } = parsePositionIdsFromSportInfoRow(userInfo || {});
        const laneIds = unique([primaryId, secondaryId]);
        if (laneIds.length === 0) {
          setPositionLaneOptions([]);
          setSelectedPositionLaneId("all");
          return;
        }
        const { data: positionRows, error: positionError } = await supabase
          .from("positions")
          .select("id, name")
          .in("id", laneIds);
        if (positionError) throw positionError;
        const byId = new Map((positionRows || []).map((row) => [String(row.id), row]));
        const builtOptions = laneIds
          .map((id, idx) => {
            const row = byId.get(String(id));
            return row ? { id: String(row.id), label: row.name, isPrimary: idx === 0 } : null;
          })
          .filter(Boolean);
        setPositionLaneOptions(builtOptions);
        setSelectedPositionLaneId((prev) => {
          if (builtOptions.length <= 1) return "all";
          const stillValid = builtOptions.some((opt) => opt.id === prev);
          return stillValid ? prev : builtOptions[0].id;
        });

      } catch (err) {
        console.error("Error fetching modeled player:", err);
        setModeledPlayer(null);
        setPositionLaneOptions([]);
        setSelectedPositionLaneId("all");
      }
    }

    fetchModeledPlayerData();
  }, [userId, selectedProgram]);

  console.log("User's Modeled Player", modeledPlayer)

  // Fetch workouts for today and last 7 days
  useEffect(() => {
    if (!selectedProgram) return;

    async function fetchWorkouts() {
      try {
        const todayEnd = dayjs().endOf("day").toISOString();
        const sevenDaysAgo = dayjs().subtract(6, "day").startOf("day").toISOString();

        const { data, error } = await supabase
          .from("skill_workouts")
          .select("*")
          .eq("athlete_id", userId)
          .eq("program_id", selectedProgram.id)
          .gte("workout_date", sevenDaysAgo)
          .lte("workout_date", todayEnd)
          .order("workout_date", { ascending: false });

        if (error) throw error;

        setRecentWorkouts(data || []);
        setTodaysWorkouts((data || []).filter((w) =>
          dayjs(w.workout_date).isSame(dayjs(), "day")
        ));
      } catch (err) {
        console.error("Error fetching workouts:", err);
      }
    }

    fetchWorkouts();
  }, [selectedProgram, userId]);

  // Fetch monthly skill overview
  useEffect(() => {
    if (!selectedProgram) return;

    const fetchMonthlyOverview = async () => {
      try {
        const today = dayjs().format("YYYY-MM-DD");

        const { data, error } = await supabase
          .from("user_monthly_skill_overviews")
          .select("*")
          .eq("user_id", userId)
          .eq("program_id", selectedProgram.id)
          .lte("start_date", today)
          .gte("end_date", today)
          .order("start_date", { ascending: true });

        if (error) throw error;

        setMonthlyOutlines(data || []);
      } catch (err) {
        console.error("Error fetching monthly overview:", err.message);
      }
    };

    fetchMonthlyOverview();
  }, [userId, selectedProgram]);

  // Auto-generate workout after payment success

  // Fetch drills for a selected workout
  const handleShowWorkoutDrills = async (workout) => {
    try {
      const { data, error } = await supabase
        .from("workout_drills")
        .select("*")
        .eq("workout_id", workout.id)
        .order("created_at", { ascending: true });

      if (error) throw error;

      setSelectedWorkout({ ...workout, drills: data || [] });
      setShowWorkoutModal(true);
    } catch (err) {
      console.error("Error fetching drills:", err);
    }
  };

  // Modal display of weekly workouts
  const handleShowMonthlyDetails = async (monthlyOverview) => {
    const weeklyData = await fetchWeeklyWorkouts(monthlyOverview.id);
    setSelectedWeeklyWorkouts(weeklyData);
    setIsModalOpen(true);
  };

  // Generate Monthly Skill Overview
  const handleGenerateMonthlyOverview = async () => {
    if (!selectedProgram) return alert("Please select a program.");
    setLoadingMonthly(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("You must be logged in.");
      const today = dayjs().format("YYYY-MM-DD");
      const laneFocusAreas = dualPositionMonthlyContext
        ? unique(dualPositionMonthlyContext.lanes.flatMap((lane) => lane.focus_items || []))
        : [];
      const selectedSwotFocusAreas = laneFocusAreas.length > 0
        ? laneFocusAreas
        : (
          Array.isArray(effectiveMonthlySwotPriorityPayload?.season_priority_focus_items)
            ? effectiveMonthlySwotPriorityPayload.season_priority_focus_items
            : getFocusAreasFromSwot(selectedSwot)
        );
      const fallbackFocusAreas = Array.isArray(selectedProgram?.focus_areas)
        ? selectedProgram.focus_areas
        : parseJsonArray(selectedProgram?.focus_areas || "[]");
      const { data: skillScores, error: scoreError } = await supabase
        .from("user_skill_scores")
        .select(`
          user_score, tag, position_id, program_goal_skill_scores!fk_user_skill_scores_program_goal_skill_score(*)
        `)
        .eq("athlete_id", userId)
        .eq("program_id", selectedProgram.id);
      if (scoreError) throw scoreError;

      const { data: programSkills, error: programError } = await supabase
        .from("program_goal_skill_scores")
        .select("target_score, skill_name, position_ids")
        .eq("program_id", selectedProgram.id);
      if (programError) throw programError;

      const userSkillScores = {};
      const positionSkillScores = {};
      const preferredPositionId = positionLaneOptions[0]?.id || null;
      const sortedSkillRows = (skillScores || []).sort((a, b) => {
        const aPref = preferredPositionId && String(a.position_id || "") === String(preferredPositionId) ? 1 : 0;
        const bPref = preferredPositionId && String(b.position_id || "") === String(preferredPositionId) ? 1 : 0;
        return aPref - bPref;
      });
      sortedSkillRows.forEach((s) => {
        const skillName = s.program_goal_skill_scores?.skill_name || s.tag;
        userSkillScores[skillName] = s.user_score;
        const posId = String(s.position_id || "").trim();
        if (posId) {
          if (!positionSkillScores[posId]) positionSkillScores[posId] = {};
          positionSkillScores[posId][skillName] = s.user_score;
        }
      });

      const programSkillScores = {};
      programSkills?.forEach((s) => {
        programSkillScores[s.skill_name] = s.target_score;
      });
      const positionProgramScores = {};
      const laneFocusCandidatesByPosition = {};
      (positionLaneOptions || []).forEach((lane) => {
        const laneId = String(lane.id || "").trim();
        if (!laneId) return;
        const laneProgramRows = (programSkills || []).filter((row) => {
          const ids = Array.isArray(row.position_ids) ? row.position_ids.map((id) => String(id)) : [];
          return ids.includes(laneId);
        });
        const laneProgramMap = {};
        laneProgramRows.forEach((row) => {
          laneProgramMap[row.skill_name] = row.target_score;
        });
        positionProgramScores[laneId] = laneProgramMap;

        const laneUserMap = positionSkillScores[laneId] || {};
        const laneGapCandidates = Object.entries(laneProgramMap)
          .map(([skillName, target]) => {
            const user = Number(laneUserMap[skillName] ?? 0);
            const targetNum = Number(target ?? 0);
            const gap = targetNum - user;
            return { skillName, gap };
          })
          .sort((a, b) => b.gap - a.gap)
          .filter((entry) => entry.gap > 0)
          .slice(0, 2)
          .map((entry) => entry.skillName);
        laneFocusCandidatesByPosition[laneId] = laneGapCandidates;
      });
      const scoreDerivedLaneFocus = unique(Object.values(laneFocusCandidatesByPosition).flat());
      const effectiveFocusAreas = unique(
        selectedSwotFocusAreas.length > 0
          ? [...selectedSwotFocusAreas, ...scoreDerivedLaneFocus]
          : [...scoreDerivedLaneFocus, ...fallbackFocusAreas]
      );

      // Fetch current month's monthly overview
      const { data: monthlyData, error: monthlyError } = await supabase
        .from("user_monthly_skill_overviews")
        .select("*")
        .eq("user_id", userId)
        .eq("program_id", selectedProgram.id)
        .lte("start_date", today)
        .gte("end_date", today)
        .order("start_date", { ascending: true });

      if (monthlyError) throw monthlyError;

      // If overview exists, use it; otherwise, generate a new one
      let overviewRow;
      if (monthlyData && monthlyData.length > 0) {
        overviewRow = monthlyData[0];
        const existingFocusAreas = parseJsonArray(overviewRow.focus_areas || "[]");
        const parsedExistingUserScores = (() => {
          try { return JSON.parse(overviewRow.user_skill_scores || "{}"); } catch (_e) { return {}; }
        })();
        const parsedExistingProgramScores = (() => {
          try { return JSON.parse(overviewRow.program_skill_scores || "{}"); } catch (_e) { return {}; }
        })();
        const hasSameFocusAreas = JSON.stringify(existingFocusAreas) === JSON.stringify(effectiveFocusAreas);
        const hasSameUserScores = JSON.stringify(parsedExistingUserScores) === JSON.stringify(userSkillScores);
        const hasSameProgramScores = JSON.stringify(parsedExistingProgramScores) === JSON.stringify(programSkillScores);
        if (!hasSameFocusAreas || !hasSameUserScores || !hasSameProgramScores) {
          const { data: updatedOverview, error: updateOverviewError } = await supabase
            .from("user_monthly_skill_overviews")
            .update({
              focus_areas: JSON.stringify(effectiveFocusAreas),
              user_skill_scores: JSON.stringify(userSkillScores),
              program_skill_scores: JSON.stringify(programSkillScores)
            })
            .eq("id", overviewRow.id)
            .select("*")
            .single();
          if (updateOverviewError) throw updateOverviewError;
          overviewRow = updatedOverview;
        }
        setMonthlyOutlines([overviewRow]);
      } else {
        // Insert new monthly overview row
        const startDate = today;
        const endDate = dayjs().add(1, "month").format("YYYY-MM-DD");

        const newOverview = {
          start_date: startDate,
          end_date: endDate,
          program_id: selectedProgram.id,
          modeled_athlete: modeledPlayer || "Unknown",
          program_skill_scores: JSON.stringify(programSkillScores),
          user_skill_scores: JSON.stringify(userSkillScores),
          user_id: userId,
          sport: selectedProgram.sport,
          focus_areas: JSON.stringify(effectiveFocusAreas),
        };

        console.log(newOverview);

        const { data: insertedOverview, error: insertError } = await supabase
          .from("user_monthly_skill_overviews")
          .insert([newOverview])
          .select("*");
        if (insertError) throw insertError;

        overviewRow = insertedOverview[0];
        setMonthlyOutlines([overviewRow]);
      }

      // Call Supabase Edge Function to generate weekly plan
      const res = await fetch(`https://kzklgxnbtquhjfywtnsz.functions.supabase.co/generateWeeklySkillPlans?plan_id=eq.${overviewRow.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          user_current_scores: JSON.parse(overviewRow.user_skill_scores || "{}"),
          program_scores: JSON.parse(overviewRow.program_skill_scores || "{}"),
          focus_areas: effectiveFocusAreas,
          start_date: overviewRow.start_date,
          end_date: dayjs(overviewRow.start_date).add(1, "week").format("YYYY-MM-DD"),
          current_age: userGrade || 11,
          user_grade: userGrade || 11,
          target_age: 18,
          previous_scores: JSON.parse(overviewRow.user_skill_scores || "{}"),
          sport: selectedProgram.sport || "basketball",
          season_phase: seasonGuidance.phase,
          season_guidance: seasonGuidance.emphasis,
          workout_intensity_target: seasonGuidance.intensity,
          workout_length_recommendation: seasonGuidance.recommendedWorkoutLength,
          baseball_focus_domain: baseballFocusDomain,
          swot_priorities: effectiveMonthlySwotPriorityPayload || null,
          swot_skill_categories: effectiveMonthlySwotPriorityPayload?.skill_categories || null,
          season_priorities: effectiveMonthlySwotPriorityPayload?.skill_priorities || [],
          completed_priority_items: effectiveMonthlySwotPriorityPayload?.completed_skill_priority_items || [],
          season_priority_focus_items: effectiveMonthlySwotPriorityPayload?.season_priority_focus_items || [],
          priority_cycle_context: {
            mode: "priority_1_micro_point_progression",
            active_priority: effectiveMonthlySwotPriorityPayload?.primary_skill_priority || null,
            active_priority_micro_points: effectiveMonthlySwotPriorityPayload?.primary_priority_action_plans || [],
            next_pending_micro_point: effectiveMonthlySwotPriorityPayload?.next_pending_micro_point || null,
            next_pending_micro_point_step: effectiveMonthlySwotPriorityPayload?.next_pending_micro_point_step || null,
            advance_rule: "Advance to next priority only after current priority is marked completed by coach."
          },
          dual_position_context: dualPositionMonthlyContext,
          position_skill_scores: positionSkillScores,
          position_program_scores: positionProgramScores,
          lane_focus_candidates: laneFocusCandidatesByPosition,
          priority_prompt_directives: priorityPromptDirectives,
          modeled_athlete: modeledPlayer,
          user_id: userId, // Pass the variable from your component props/state
          program_id: selectedProgram.id
        }),
      });

      const edgeData = await res.json();
      const weeklyPlanFromAI = (() => {
        // New shape: JSON object from edge function.
        if (edgeData && typeof edgeData === "object" && Array.isArray(edgeData.p_week_json)) {
          return edgeData;
        }
        // Legacy shape: OpenAI wrapper payload.
        const rawContent = edgeData?.choices?.[0]?.message?.content;
        if (!rawContent) {
          throw new Error("Weekly plan response was missing generated content.");
        }
        const cleanedContent = String(rawContent).replace(/```json/g, "").replace(/```/g, "").trim();
        return JSON.parse(cleanedContent);
      })();
      const monthlyStartDate = dayjs(overviewRow.start_date);

      // Save weekly plan to Supabase
      const rowsToInsert = weeklyPlanFromAI.p_week_json.map((week, index) => {
        // Calculate the week number (Use index + 1 as fallback if AI returns null/0)
        const currentWeekNum = Number(week.week_number) || (index + 1);

        // Calculate start date based on the Monthly Overview Start Date + (Week Number - 1) weeks
        const calculatedStartDate = monthlyStartDate.add(currentWeekNum - 1, 'week');
        // Format the calculated start date
        return {
          start_date: calculatedStartDate.format("YYYY-MM-DD"),
          // End date is 6 days after start date (e.g., Monday to Sunday)
          end_date: calculatedStartDate.add(6, "day").format("YYYY-MM-DD"),
          week_number: Number(week.week_number),
          month_number: Number(week.month_number),
          focus_areas: week.focus_areas,
          previous_scores: week.previous_scores,
          notes: week.notes,
          user_id: userId,
          followed_program: selectedProgram.id,
          created_at: dayjs().toISOString(),
          plan_id: overviewRow.id
        }
      });

      // Check for existing weekly plans
      const { data: existingWeeks } = await supabase
        .from("weekly_skill_workouts")
        .select("*")
        .eq("plan_id", overviewRow.id);

      if (existingWeeks && existingWeeks.length > 0) {
        console.log("Weekly plan already exists for this monthly overview. Skipping insert.");
        setWeeklyPlan(existingWeeks);
        alert("Weekly plan already exists for this monthly overview.");
        return;
      }

      const { data: insertedWeeks, error: weeklyInsertError } = await supabase
        .from("weekly_skill_workouts")
        .insert(rowsToInsert)
        .select();
      if (weeklyInsertError) throw weeklyInsertError;

      setWeeklyPlan(insertedWeeks);
      alert("Monthly overview generated and weekly plan saved!");
    } catch (err) {
      console.error("Error generating monthly overview and weekly plan:", err);
      alert("Failed to generate monthly overview or weekly plan.");
    } finally {
      setLoadingMonthly(false);
    }
  };

  // Generate Daily Skill Workout
  const generateDailyWorkout = async () => {
    if (!selectedProgram || !monthlyOutlines[0]) {
      return alert("Please select a program and monthly overview.");
    }

    setLoading(true);
    const today = dayjs().format("YYYY-MM-DD");
    const monthlyOverview = monthlyOutlines[0];
    const dailyLane = effectiveDailyLane || null;
    const selectedSwotFocusAreas = Array.isArray(effectiveSwotPriorityPayloadForDaily?.season_priority_focus_items)
      ? effectiveSwotPriorityPayloadForDaily.season_priority_focus_items
      : getFocusAreasFromSwot(selectedSwot);
    const monthlyFocusAreas = parseJsonArray(monthlyOverview.focus_areas || "[]");
    const combinedFocusAreas = selectedSwotFocusAreas.length > 0 ? selectedSwotFocusAreas : monthlyFocusAreas;
    const effectiveFocusAreas = selectedProgramSport === "baseball"
      ? filterFocusAreasByBaseballDomain(combinedFocusAreas, baseballFocusDomain)
      : combinedFocusAreas;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("You must be logged in.");
      // Insert base skill_workouts row for today
      const { data: workoutRow, error: workoutError } = await supabase
        .from("skill_workouts")
        .insert({
          athlete_id: userId,
          program_id: selectedProgram.id,
          workout_date: today,
          duration_minutes: effectiveWorkoutLength,
          focus: effectiveFocusAreas.join(", "),
          ai_generated: true,
          created_at: dayjs().toISOString(),
        })
        .select()
        .single();
      if (workoutError) throw workoutError;

      // Fetch weekly_skill_workouts row for today
      const { data: weeklyRow, error: weeklyError } = await supabase
        .from("weekly_skill_workouts")
        .select("*")
        .eq("plan_id", monthlyOverview.id)
        .lte("start_date", today)
        .gte("end_date", today)
        .limit(1)
        .single();
      if (weeklyError) throw weeklyError;

      // Fetch last 7 days of performed drills
      const sevenDaysAgo = dayjs().subtract(6, "day").startOf("day").toISOString();
      const todayISO = dayjs().endOf("day").toISOString();

      const { data: recentDrills } = await supabase
        .from("workout_drills")
        .select("*")
        .gte("created_at", sevenDaysAgo)
        .lte("created_at", todayISO)
        .order("created_at", { ascending: true });

      // Build previous_scores object
      const previousScores = recentDrills.reduce((acc, d) => {
        acc[d.drill_name] = d.duration_minutes;
        return acc;
      }, {});

      // Pull combine metrics and athlete scores for the selected domain.
      const { data: programCombineRows, error: programCombineError } = await supabase
        .from("program_goal_combine_scores")
        .select("id, drill_name, target_score")
        .eq("program_id", selectedProgram.id);
      if (programCombineError) throw programCombineError;

      const { data: userCombineRows, error: userCombineError } = await supabase
        .from("user_combine_score")
        .select("program_combine_scores, user_score, position_id")
        .eq("athlete_id", userId)
        .eq("program_id", selectedProgram.id);
      if (userCombineError) throw userCombineError;

      const { data: skillScoreRows, error: skillScoreError } = await supabase
        .from("user_skill_scores")
        .select(`
          user_score, position_id, tag, program_goal_skill_scores!fk_user_skill_scores_program_goal_skill_score(skill_name)
        `)
        .eq("athlete_id", userId)
        .eq("program_id", selectedProgram.id);
      if (skillScoreError) throw skillScoreError;

      const { data: programSkillRows, error: programSkillError } = await supabase
        .from("program_goal_skill_scores")
        .select("skill_name, target_score, position_ids")
        .eq("program_id", selectedProgram.id);
      if (programSkillError) throw programSkillError;

      const laneId = String(dailyLane?.id || "").trim();
      const laneProgramRows = laneId
        ? (programSkillRows || []).filter((row) => {
          const ids = Array.isArray(row.position_ids) ? row.position_ids.map((id) => String(id)) : [];
          return ids.length === 0 || ids.includes(laneId);
        })
        : (programSkillRows || []);
      const laneProgramScores = {};
      laneProgramRows.forEach((row) => {
        laneProgramScores[row.skill_name] = row.target_score;
      });

      const laneUserRows = laneId
        ? (skillScoreRows || []).filter((row) => {
          const rowPos = String(row.position_id || "").trim();
          return rowPos === laneId || rowPos === "";
        })
        : (skillScoreRows || []);
      laneUserRows
        .sort((a, b) => (a.position_id ? 1 : 0) - (b.position_id ? 1 : 0))
        .forEach((row) => {
          const skillName = row.program_goal_skill_scores?.skill_name || row.tag;
          if (!skillName) return;
          laneProgramScores[skillName] = laneProgramScores[skillName] ?? 0;
        });
      const laneUserScores = {};
      laneUserRows
        .sort((a, b) => (a.position_id ? 1 : 0) - (b.position_id ? 1 : 0))
        .forEach((row) => {
          const skillName = row.program_goal_skill_scores?.skill_name || row.tag;
          if (!skillName) return;
          laneUserScores[skillName] = row.user_score;
        });

      const filteredProgramCombineRows = selectedProgramSport === "baseball"
        ? filterCombineRowsByBaseballDomain(programCombineRows || [], baseballFocusDomain, "drill_name")
        : (programCombineRows || []);
      const filteredCombineIds = new Set(filteredProgramCombineRows.map((row) => String(row.id)));
      const filteredUserCombineRows = laneId
        ? (userCombineRows || [])
          .filter((row) => {
            const rowPos = String(row.position_id || "").trim();
            return rowPos === laneId || rowPos === "";
          })
          .sort((a, b) => (a.position_id ? 1 : 0) - (b.position_id ? 1 : 0))
        : (userCombineRows || []);
      const userCombineByMetricId = Object.fromEntries(
        filteredUserCombineRows.map((row) => [String(row.program_combine_scores || ""), row.user_score])
      );
      const programCombineScores = {};
      const userCombineScores = {};
      filteredProgramCombineRows.forEach((row) => {
        const drillName = String(row.drill_name || "").trim();
        if (!drillName) return;
        programCombineScores[drillName] = row.target_score;
        const metricId = String(row.id || "");
        if (filteredCombineIds.has(metricId) && userCombineByMetricId[metricId] !== undefined) {
          userCombineScores[drillName] = userCombineByMetricId[metricId];
        }
      });

      // Call Edge Function to generate drills
      const userCurrentScores = Object.keys(laneUserScores).length > 0
        ? laneUserScores
        : JSON.parse(monthlyOverview.user_skill_scores || "{}");
      const programScores = Object.keys(laneProgramScores).length > 0
        ? laneProgramScores
        : JSON.parse(monthlyOverview.program_skill_scores || "{}");
      const filteredUserCurrentScores = selectedProgramSport === "baseball"
        ? filterMapByBaseballDomain(userCurrentScores, baseballFocusDomain)
        : userCurrentScores;
      const filteredProgramScores = selectedProgramSport === "baseball"
        ? filterMapByBaseballDomain(programScores, baseballFocusDomain)
        : programScores;
      const payload = {
        user_current_scores: filteredUserCurrentScores,
        program_scores: filteredProgramScores,
        focus_areas: effectiveFocusAreas,
        previous_scores: previousScores,
        weekly_row_data: weeklyRow,
        start_date: today,
        current_age: userGrade || 11,
        user_grade: userGrade || 11,
        target_age: selectedProgram.player_peaking || 18,
        sport: selectedProgram.sport || "basketball",
        season_phase: seasonGuidance.phase,
        season_guidance: seasonGuidance.emphasis,
        workout_intensity_target: seasonGuidance.intensity,
        workout_length_recommendation: seasonGuidance.recommendedWorkoutLength,
        baseball_focus_domain: baseballFocusDomain,
        swot_priorities: effectiveSwotPriorityPayloadForDaily || null,
        swot_skill_categories: effectiveSwotPriorityPayloadForDaily?.skill_categories || null,
        season_priorities: effectiveSwotPriorityPayloadForDaily?.skill_priorities || [],
        completed_priority_items: effectiveSwotPriorityPayloadForDaily?.completed_skill_priority_items || [],
        season_priority_focus_items: effectiveSwotPriorityPayloadForDaily?.season_priority_focus_items || [],
        priority_cycle_context: {
          mode: "priority_1_micro_point_progression",
          active_priority: effectiveSwotPriorityPayloadForDaily?.primary_skill_priority || null,
          active_priority_micro_points: effectiveSwotPriorityPayloadForDaily?.primary_priority_action_plans || [],
          next_pending_micro_point: effectiveSwotPriorityPayloadForDaily?.next_pending_micro_point || null,
          next_pending_micro_point_step: effectiveSwotPriorityPayloadForDaily?.next_pending_micro_point_step || null,
          advance_rule: "Advance to next priority only after current priority is marked completed by coach."
        },
        user_combine_scores: userCombineScores,
        program_combine_scores: programCombineScores,
        strict_domain_focus: selectedProgramSport === "baseball" && baseballFocusDomain !== "all",
        selected_position_lane: dailyLane
          ? {
            id: dailyLane.id,
            label: dailyLane.label,
            preferred_domains: selectedProgramSport === "baseball"
              ? inferBaseballDomainsFromPositionLabel(dailyLane.label)
              : [],
          }
          : null,
        priority_prompt_directives: priorityPromptDirectives,
        workout_length: effectiveWorkoutLength,
      };

      const res = await fetch(
        "https://kzklgxnbtquhjfywtnsz.functions.supabase.co/generateSkillWorkouts",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${session.access_token}`
          },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error("Failed to generate workout. Check Edge Function logs.");

      const data = await res.json();
      const workoutJSON = (() => {
        // New edge-function shape: returns workout JSON directly.
        if (data && typeof data === "object" && data.blocks) return data;

        // Backward compatibility: OpenAI wrapper shape.
        const rawContent = data?.choices?.[0]?.message?.content;
        if (!rawContent) {
          throw new Error("Workout response was missing generated content.");
        }
        const cleaned = String(rawContent).replace(/```json/g, "").replace(/```/g, "").trim();
        return JSON.parse(cleaned);
      })();

      // Insert drills into workout_drills
      const blocks = workoutJSON?.blocks && typeof workoutJSON.blocks === "object"
        ? workoutJSON.blocks
        : {};
      const allDrills = [
        ...(Array.isArray(blocks["Block 1 — Review and Reinforce"]) ? blocks["Block 1 — Review and Reinforce"] : []),
        ...(Array.isArray(blocks["Block 2 — Progression Drills"]) ? blocks["Block 2 — Progression Drills"] : []),
        ...(Array.isArray(blocks["Block 3 — Game Situation Integration"]) ? blocks["Block 3 — Game Situation Integration"] : []),
        ...Object.values(blocks).flatMap((value) => (Array.isArray(value) ? value : [])),
      ]
        .filter((d, idx, arr) => idx === arr.findIndex((x) => JSON.stringify(x) === JSON.stringify(d)))
        .map((d) => ({
        workout_id: workoutRow.id,
        drill_name: String(d?.drill_name || "Skill Drill").trim(),
        description: d?.description || "",
        duration: Number(d?.duration_minutes || d?.duration || 0),
        sets: Number(d?.sets || 1),
        reps: Array.isArray(d?.reps) ? d.reps : [Number(d?.reps || 1)],
        created_at: dayjs().toISOString(),
      }));

      // Filter duplicates by drill_name
      const drillsToInsert = allDrills.filter(
        (drill, index, arr) =>
          index === arr.findIndex((t) => t.drill_name === drill.drill_name)
      );

      const { data: insertedDrills, error: insertError } = await supabase
        .from("workout_drills")
        .insert(drillsToInsert);

      if (insertError) {
        console.error("Insert error:", insertError);
      } else {
        console.log("Drills inserted successfully:", insertedDrills);
      }

      // Update state for UI
      setDailyWorkout(workoutJSON);
    } catch (err) {
      console.error("Error generating daily workout:", err.message);
      alert("Failed to generate daily workout.");
    } finally {
      setLoading(false);
    }
  };

  // --- 1. Find Active Monthly Program ---
  const activeMonthlyProgram = monthlyOutlines.find((program) => {
    const now = dayjs();
    const start = dayjs(program.start_date);
    const end = dayjs(program.end_date);
    return (now.isSame(start, 'day') || now.isAfter(start, 'day')) &&
      (now.isSame(end, 'day') || now.isBefore(end, 'day'));
  });

  // --- Icons (Inline for UI consistency) ---
  const Icons = {
    Calendar: () => (
      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
    ),
    User: () => (
      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
    ),
    Clock: () => (
      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
    ),
    Close: () => (
      <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
    ),
    Info: () => (
      <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
    ),
    Dumbbell: () => (
      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" /></svg>
    ),
    ChevronRight: () => (
      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
    ),
  };

  return (
    <div className="min-h-screen bg-gray-100 pb-20">

      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm sticky top-0 z-10">
        <h1 className="text-xl font-extrabold text-gray-900 text-center tracking-tight">
          Skill Workouts Creator
        </h1>
      </div>

      <div className="max-w-3xl mx-auto p-4 space-y-6">

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-xs font-bold text-blue-600 uppercase tracking-wider">Offseason Development</p>
            <p className="text-sm text-slate-600 mt-1">
              Buy a full development program for your selected team.
            </p>
          </div>
          <button
            onClick={() => selectedProgram?.id && navigate(`/athlete/offseason-program?programId=${selectedProgram.id}`)}
            disabled={!selectedProgram?.id}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-5 rounded-xl shadow transition-transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Buy Offseason Program
          </button>
        </div>

        {/* --- SECTION 1: Settings (De-emphasized) --- */}
        <div className="bg-gray-50/50 rounded-2xl p-4 border border-gray-200/50">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 px-1">Settings</h3>
          <div className={`grid grid-cols-1 gap-3 ${selectedProgramSport === "baseball" ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
            {selectedProgramSport === "baseball" ? (
              <div className="bg-white rounded-xl p-3 flex items-center gap-3 border border-gray-100 shadow-sm">
                <div className="bg-gray-100 p-2 rounded-lg">
                  <Icons.Info />
                </div>
                <div className="flex-1">
                  <label className="text-[10px] font-semibold text-gray-500 uppercase block mb-2">Today's Baseball Focus</label>
                  <select
                    className="appearance-none w-full bg-white border border-gray-200 text-gray-900 text-sm font-semibold rounded-xl px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    value={baseballFocusDomain}
                    onChange={(e) => setBaseballFocusDomain(e.target.value)}
                  >
                    <option value="all">Balanced (All)</option>
                    <option value="hitting">Hitting</option>
                    <option value="pitching">Pitching</option>
                    <option value="fielding">Fielding</option>
                  </select>
                </div>
              </div>
            ) : null}
            <div className="bg-white rounded-xl p-3 flex items-center gap-3 border border-gray-100 shadow-sm">
              <div className="bg-gray-100 p-2 rounded-lg">
                <Icons.User />
              </div>
              <div className="flex-1">
                <label className="text-[10px] font-semibold text-gray-500 uppercase block">Select SWOT Analysis ({selectedProgram?.sport || "Sport"})</label>
                <div className="relative">
                  <select
                    className="appearance-none w-full bg-white border border-gray-200 text-gray-900 text-sm font-semibold rounded-xl px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-gray-50 disabled:text-gray-400"
                    value={selectedSwotId}
                    onChange={(e) => setSelectedSwotId(e.target.value)}
                    disabled={!selectedProgram || (swotsForSelectedProgram.length === 0 && swotsForSelectedSport.length === 0)}
                  >
                    {(swotsForSelectedProgram.length === 0 && swotsForSelectedSport.length === 0) ? (
                      <option value="">No SWOTs found for this sport</option>
                    ) : (
                      (swotsForSelectedProgram.length > 0 ? swotsForSelectedProgram : swotsForSelectedSport).map((swot) => (
                        <option key={swot.id} value={swot.id}>
                          {`${swot.programs?.name || "Program SWOT"}${swot.position?.name ? ` • ${swot.position.name}` : ""}`}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-3 flex items-center gap-3 border border-gray-100 shadow-sm">
              <div className="bg-gray-100 p-2 rounded-lg">
                <Icons.Clock />
              </div>
              <div className="flex-1">
                <label className="text-[10px] font-semibold text-gray-500 uppercase block">Duration (Min)</label>
                <input
                  type="number"
                  value={workoutLength}
                  onChange={(e) => setWorkoutLength(e.target.value)}
                  className="w-full text-gray-900 font-bold text-base bg-transparent border-none p-0 focus:ring-0"
                />
              </div>
            </div>

          </div>
        </div>

        {/* --- SECTION 2: THE CENTERPIECE (Generation Tools) --- */}
        <div className="bg-white rounded-3xl p-6 shadow-2xl shadow-blue-200/60 border border-blue-50 relative overflow-hidden max-w-xl mx-auto">

          {/* Decorative BG Element */}
          <div className="absolute top-0 right-0 -mt-10 -mr-10 text-blue-50 opacity-50 pointer-events-none">
            <svg className="w-48 h-48" fill="currentColor" viewBox="0 0 24 24"><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
          </div>

          <div className="relative z-10 space-y-6">

            {/* Info Bar / Pro Tip */}
            {!activeMonthlyProgram && (
              <div className="bg-blue-50/80 border border-blue-100 p-3 rounded-xl flex items-start gap-3">
                <div className="mt-0.5 shrink-0"><Icons.Info /></div>
                <div>
                  <p className="font-bold text-blue-900 text-xs uppercase tracking-wide">Pro Tip</p>
                  <p className="text-blue-800 text-sm mt-0.5 leading-snug">
                    Generate a <span className="font-bold">Monthly Program</span> below for the best results.
                  </p>
                </div>
              </div>
            )}

            {/* Program Selector */}
            <div>
              <label className="text-sm font-bold text-gray-700 mb-2 block text-center">
                Select Program
              </label>
              <div className="relative">
                <select
                  className="appearance-none w-full bg-blue-50/50 border border-blue-100 text-gray-900 text-xl font-bold rounded-2xl px-6 py-4 text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  value={selectedProgram?.id || ""}
                  onChange={(e) => {
                    const programId = e.target.value;
                    const program = programs.find((p) => p.program_id === programId);
                    if (program) {
                      setSelectedProgram({
                        ...(program.programs || {}),
                        id: program.program_id,
                      });
                    }
                  }}
                >
                  <option value="" disabled>-- Select --</option>
                  {programs.map((p) => (
                    <option key={p.program_id} value={p.program_id}>
                      {p.programs.name}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-blue-500">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7"></path></svg>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Purchase / Daily Button */}
              {checkingProgramWorkoutAccess ? (
                <div className="w-full bg-white border border-gray-200 text-gray-500 text-sm font-semibold py-3 rounded-xl text-center">
                  Checking workout access...
                </div>
              ) : hasProgramWorkoutAccess ? (
                <button
                  onClick={generateDailyWorkout}
                  disabled={loadingMonthly || loading}
                  className="w-full bg-blue-600 text-white font-bold py-3 rounded-xl hover:bg-blue-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Generating..." : "Generate Daily Workout"}
                </button>
              ) : (
                <div className="w-full [&>button]:w-full [&>button]:py-4 [&>button]:text-lg [&>button]:rounded-2xl [&>button]:shadow-lg [&>button]:font-extrabold">
                  <ExpressPurchaseButton
                    userId={userId}
                    userGrade={userGrade}
                    selectedSwot={effectiveSwotPriorityPayloadForDaily || selectedSwotPayload}
                    baseballFocusDomain={baseballFocusDomain}
                    selectedPositionLane={hasSecondaryPosition ? (effectiveDailyLane || selectedPositionLane) : null}
                    program={selectedProgram}
                    monthlyOverview={monthlyOutlines ? monthlyOutlines[0] : null}
                    workoutLength={effectiveWorkoutLength}
                    onWorkoutGenerated={(generatedJson) => {
                      setDailyWorkout(generatedJson);
                      alert("Workout generated successfully!");
                    }}
                  />
                </div>
              )}

              {/* Monthly Overview Button */}
              {!activeMonthlyProgram && (
                <button
                  onClick={handleGenerateMonthlyOverview}
                  disabled={loadingMonthly || loadingDaily}
                  className="w-full bg-white border-2 border-blue-100 text-blue-600 font-bold py-3 rounded-xl hover:bg-blue-50 hover:border-blue-200 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingMonthly ? "Generating..." : "Generate Monthly Overview"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* --- SECTION 3: Monthly Overview Cards --- */}
        {monthlyOutlines.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-bold text-gray-800 mb-3 px-1">Monthly Overviews</h2>
            <div className="grid grid-cols-1 gap-4">
              {monthlyOutlines.map((overview) => (
                <div
                  key={overview.id}
                  onClick={() => handleShowMonthlyDetails(overview)}
                  className="group bg-white rounded-2xl p-5 border border-gray-100 shadow-sm cursor-pointer relative overflow-hidden transition-all hover:shadow-md active:scale-[0.99]"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1 block">
                        {dayjs(overview.start_date).format("MMM YYYY")}
                      </span>
                      <h3 className="text-lg font-bold text-gray-900 leading-tight">
                        {overview.modeled_athlete} Model
                      </h3>
                      <p className="text-gray-500 text-sm mt-1">
                        {dayjs(overview.start_date).format("MMM D")} - {dayjs(overview.end_date).format("MMM D")}
                      </p>

                      <div className="flex flex-wrap gap-2 mt-3">
                        {JSON.parse(overview.focus_areas || "[]").slice(0, 3).map((f, i) => (
                          <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md border border-gray-200">
                            {f}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="bg-blue-50 p-2 rounded-full text-blue-500 group-hover:bg-blue-100 transition-colors">
                      <Icons.ChevronRight />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* --- SECTION 4: Today's Workout & History --- */}
        <div className="mt-8 space-y-8">
          {/* Today's Workout */}
          <section>
            <h2 className="text-lg font-bold text-gray-800 mb-3 px-1">Today's Workout</h2>
            {todaysWorkouts.length === 0 ? (
              <div className="bg-white rounded-xl p-5 flex items-center justify-between border border-gray-100 shadow-sm">
                <div>
                  <p className="text-gray-800 font-bold">No workouts generated for today</p>
                </div>
                <div className="bg-gray-100 p-2 rounded-full"><Icons.Dumbbell /></div>
              </div>
            ) : (
              <div className="space-y-3">
                {todaysWorkouts.map((w) => (
                  <div
                    key={w.id}
                    onClick={() => handleShowWorkoutDrills(w)}
                    className="group bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-5 shadow-lg shadow-blue-200 text-white cursor-pointer relative overflow-hidden transition-transform active:scale-[0.98]"
                  >
                    <div className="absolute top-0 right-0 p-4 opacity-10">
                      <svg className="w-24 h-24 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                    </div>
                    <div className="relative z-10 flex justify-between items-center">
                      <div>
                        <span className="inline-block bg-white/20 px-2 py-1 rounded text-xs font-semibold mb-2 backdrop-blur-sm">
                          {dayjs(w.workout_date).format("MMM D")}
                        </span>
                        <h3 className="text-xl font-bold leading-tight mb-1">{w.focus}</h3>
                        <p className="text-blue-100 text-sm">Duration: {w.duration_minutes} min</p>
                      </div>
                      <div className="bg-white/20 p-2 rounded-full backdrop-blur-sm group-hover:bg-white/30 transition-colors">
                        <Icons.ChevronRight />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* --- MODAL 1: Daily Workout Generation --- */}
      {(loadingDaily || dailyWorkout) && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 sm:p-0">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" onClick={() => { setDailyWorkout(null); setLoadingDaily(false); }}></div>
          <div className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden relative z-10 flex flex-col max-h-[85vh] animate-in slide-in-from-bottom-10 fade-in duration-300">

            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0 z-20">
              <div>
                <h2 className="text-xl font-extrabold text-slate-900 tracking-tight">Today's Session</h2>
                <p className="text-xs text-slate-400 font-medium mt-0.5">AI-Generated Skill Plan</p>
              </div>
              <button onClick={() => { setDailyWorkout(null); setLoadingDaily(false); }} className="p-2 bg-slate-50 text-slate-400 rounded-full hover:bg-slate-100 hover:text-slate-600 transition-colors">
                <Icons.Close />
              </button>
            </div>

            <div className="p-6 overflow-y-auto bg-slate-50/50 space-y-6">
              {loadingDaily && (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-slate-600 font-medium animate-pulse">Analyzing stats & building plan...</p>
                </div>
              )}

              {dailyWorkout && (
                <>
                  {/* Workout Meta Card */}
                  <div className="text-center bg-white p-5 rounded-2xl border border-blue-100 shadow-sm">
                    <h2 className="text-2xl font-black text-slate-800 mb-1">{dailyWorkout.date}</h2>
                    <p className="text-blue-600 font-bold uppercase text-xs tracking-widest mb-4">Day {dailyWorkout.day_of_week}</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {dailyWorkout.focus_areas.map((area, idx) => (
                        <span key={idx} className="bg-slate-100 text-slate-600 px-3 py-1 rounded-md text-xs font-bold border border-slate-200">
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Render Blocks (Styled like Modal 3) */}
                  {Object.keys(dailyWorkout.blocks).map((blockName, blockIdx) => (
                    <div key={blockIdx}>
                      <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3 px-1">{blockName}</h3>
                      <div className="space-y-3">
                        {dailyWorkout.blocks[blockName].map((drill, idx) => (
                          <div key={idx} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                            <p className="font-bold text-slate-800 text-lg leading-tight mb-1">{drill.drill_name}</p>
                            <p className="text-slate-500 text-sm leading-relaxed mb-4">{drill.description}</p>
                            <div className="flex flex-wrap gap-2">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-blue-50 border border-blue-100 text-blue-700 text-xs font-bold">
                                {drill.duration_minutes} min
                              </span>
                              <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-100 border border-slate-200 text-slate-600 text-xs font-bold">
                                {drill.sets} Sets
                              </span>
                              <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-100 border border-slate-200 text-slate-600 text-xs font-bold">
                                Reps: {Array.isArray(drill.reps) ? drill.reps.join(", ") : drill.reps}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL 2: Monthly Weekly Breakdown --- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 sm:p-0">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" onClick={() => setIsModalOpen(false)}></div>
          <div className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden relative z-10 flex flex-col max-h-[85vh] animate-in slide-in-from-bottom-10 fade-in duration-300">

            <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0 z-20">
              <div>
                <h3 className="text-xl font-extrabold text-slate-800 tracking-tight">Weekly Breakdown</h3>
                <p className="text-xs text-slate-400 font-medium mt-0.5">Your roadmap for this month</p>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="p-2 bg-slate-50 text-slate-400 rounded-full hover:bg-slate-100 hover:text-slate-600 transition-colors">
                <Icons.Close />
              </button>
            </div>

            <div className="p-6 overflow-y-auto bg-slate-50/50 space-y-4">
              {weeklyLaneLegend.length > 0 ? (
                <div className="bg-white rounded-xl border border-gray-100 px-4 py-3 shadow-sm">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Position Color Key</p>
                  <div className="flex flex-wrap gap-2">
                    {weeklyLaneLegend.map((lane) => (
                      <span key={lane.id} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border ${lane.chip}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${lane.dot}`}></span>
                        {lane.label}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
              {selectedWeeklyWorkouts.length === 0 ? (
                <div className="text-center py-10">
                  <div className="bg-slate-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 text-slate-400">
                    <Icons.Calendar />
                  </div>
                  <p className="text-slate-500 font-medium">No weekly data found.</p>
                </div>
              ) : (
                selectedWeeklyWorkouts.map((week) => (
                  <div key={week.id} className="group bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="px-5 py-4 flex justify-between items-center border-b border-gray-50 bg-white">
                      <div className="bg-blue-50 text-blue-700 font-extrabold text-sm px-3 py-1.5 rounded-lg">
                        WEEK {week.week_number}
                      </div>
                      <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                        {dayjs(week.start_date).format("MMM D")} — {dayjs(week.end_date).format("MMM D")}
                      </span>
                    </div>
                    <div className="p-5">
                      <div className="mb-4">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Primary Focus</p>
                        <div className="flex flex-wrap gap-2">
                          {toFocusAreaArray(week.focus_areas).map((area, idx) => (
                            <span key={idx} className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold border ${focusAreaChipClass(area)}`}>
                              {area.trim()}
                            </span>
                          ))}
                        </div>
                      </div>
                      {week.notes && (
                        <div className="relative pl-4 mt-4">
                          {/* Accent Bar */}
                          <div className="absolute left-0 top-1 bottom-1 w-1 bg-blue-400 rounded-full"></div>

                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                            Coach's Notes
                          </p>

                          <ul className="space-y-2">
                            {(() => {
                              // 1. Safely parse the notes whether they are a String, JSON String, or Array
                              let notesArray = [];
                              try {
                                if (Array.isArray(week.notes)) {
                                  notesArray = week.notes;
                                } else if (typeof week.notes === 'string' && week.notes.trim().startsWith('[')) {
                                  notesArray = JSON.parse(week.notes);
                                } else {
                                  notesArray = [week.notes]; // Handle single string case
                                }
                              } catch (e) {
                                notesArray = [week.notes]; // Fallback
                              }

                              // 2. Render list items
                              return notesArray.map((note, nIdx) => (
                                <li key={nIdx} className="flex items-start gap-2 text-sm text-slate-600 leading-relaxed">
                                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-300 shrink-0"></span>
                                  <span>{note}</span>
                                </li>
                              ));
                            })()}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL 3: View Drills (Workout Details) --- */}
      {showWorkoutModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 sm:p-0">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" onClick={() => setShowWorkoutModal(false)}></div>
          <div className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden relative z-10 flex flex-col max-h-[85vh] animate-in slide-in-from-bottom-10 fade-in duration-300">

            <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-start bg-white sticky top-0 z-20">
              <div className="pr-4">
                <h2 className="text-xl font-extrabold text-slate-900 leading-tight tracking-tight">
                  {selectedWorkout?.focus}
                </h2>
                <div className="flex items-center gap-3 mt-1.5">
                  <div className="flex items-center gap-1.5 text-slate-500">
                    <Icons.Calendar />
                    <span className="text-xs font-bold uppercase tracking-wide">
                      {dayjs(selectedWorkout?.workout_date).format("MMM D, YYYY")}
                    </span>
                  </div>
                  <span className="text-slate-300">•</span>
                  <div className="flex items-center gap-1.5 text-blue-600">
                    <Icons.Clock />
                    <span className="text-xs font-bold uppercase tracking-wide">
                      {selectedWorkout?.duration_minutes} Min
                    </span>
                  </div>
                </div>
              </div>
              <button onClick={() => setShowWorkoutModal(false)} className="p-2 bg-slate-50 text-slate-400 rounded-full hover:bg-slate-100 hover:text-slate-600 transition-colors shrink-0">
                <Icons.Close />
              </button>
            </div>

            <div className="p-6 overflow-y-auto bg-slate-50/50 space-y-4">
              {selectedWorkout?.drills?.length === 0 ? (
                <div className="text-center py-10">
                  <div className="bg-slate-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 text-slate-400">
                    <Icons.Dumbbell />
                  </div>
                  <p className="text-slate-500 font-medium">No drills found for this workout.</p>
                </div>
              ) : (
                selectedWorkout.drills.map((d, idx) => (
                  <div key={d.id} className="group bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200">
                    <div className="flex gap-4">
                      <div className="shrink-0">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-50 text-blue-600 font-extrabold text-sm border border-blue-100">
                          {idx + 1}
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-slate-800 text-lg leading-tight mb-1">{d.drill_name}</p>
                        <p className="text-slate-500 text-sm leading-relaxed mb-4">{d.description}</p>
                        <div className="flex flex-wrap gap-2">
                          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-50 border border-blue-100 text-blue-700">
                            <Icons.Clock />
                            <span className="text-xs font-bold">{d.duration} min</span>
                          </div>
                          <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-100 border border-slate-200 text-slate-600">
                            <span className="text-xs font-bold">{d.sets} Sets</span>
                          </div>
                          {d.reps && (
                            <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-100 border border-slate-200 text-slate-600">
                              <span className="text-xs font-bold">
                                {Array.isArray(d.reps) ? d.reps.join(", ") : d.reps} Reps
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="p-4 border-t border-gray-100 bg-white">
              <button onClick={() => setShowWorkoutModal(false)} className="w-full py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-black transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
};
